import { DOWNLOAD_FILENAME } from "../../constants"
import { usePhone } from "../../theme/PhoneContext"
import { useTheme } from "../../theme/useTheme"
import { openPdf } from "../../utils/pdfLock"
import { isPhoneComplete } from "../../utils/phone"
import { SocialRow } from "../ui/SocialRow"
import { AudienceToggle, PhoneToggle, SkillsToggle } from "../ui/Toggles"
import { Box, Button, Drawer, Typography } from "@mui/material"
import React, { useEffect, useState } from "react"

interface SidebarProps {
    open: boolean
    onClose: () => void
    isMediumScreen: boolean
}

export const Sidebar: React.FC<SidebarProps> = ({ open, onClose, isMediumScreen }) => {
    const { themeSettings, layout, breakpoints, audience, skills } = useTheme()
    const phone = usePhone()
    const callToActionWidth = 250

    // "download = what you see": the current site state picks the variant
    const variantStem = `resume.${audience.mode}.${skills.style}.${
        themeSettings.isDarkMode ? "dark" : "light"
    }`
    const wantsPhoneEdition = phone.show && isPhoneComplete(phone.code, phone.rest)

    const [gateState, setGateState] = useState<"idle" | "working" | "failed">("idle")

    const downloadBlob = (bytes: BlobPart) => {
        const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }))
        const anchor = document.createElement("a")
        anchor.href = url
        anchor.download = DOWNLOAD_FILENAME
        anchor.click()
        // revoke well after the save dialog has taken the bytes
        setTimeout(() => URL.revokeObjectURL(url), 10_000)
    }

    const downloadPublic = async () => {
        const response = await fetch(`/pdf/${variantStem}.pdf`)
        if (!response.ok) throw new Error(`fetch failed: ${response.status}`)
        // a misconfigured server SPA-fallbacks /pdf/* to index.html — that
        // must surface as an error, not download as a broken resume.pdf
        const type = response.headers.get("content-type") ?? ""
        if (!type.includes("application/pdf")) throw new Error(`not a pdf: ${type}`)
        downloadBlob(await response.arrayBuffer())
    }

    // phone edition: the number already typed into the phone box is the gate
    // key — fetch the sealed envelope and decrypt it right here; GCM auth
    // proves the number matches without any second prompt
    const downloadPhoneEdition = async () => {
        const response = await fetch(`/pdf/${variantStem}.phone.enc`)
        if (!response.ok) throw new Error(`fetch failed: ${response.status}`)
        const type = response.headers.get("content-type") ?? ""
        if (type.includes("text/html")) throw new Error(`not an envelope: ${type}`)
        const opened = await openPdf(await response.arrayBuffer(), `${phone.code}${phone.rest}`)
        downloadBlob(opened)
    }

    const handleDownloadClick = () => {
        setGateState("working")
        const task = wantsPhoneEdition ? downloadPhoneEdition() : downloadPublic()
        task.then(
            () => setGateState("idle"),
            (error) => {
                // a number that doesn't open the phone edition silently falls
                // back to the public edition — the visitor still gets a resume
                if (error instanceof Error && error.message === "WRONG_GATE_INPUT")
                    return downloadPublic().then(
                        () => setGateState("idle"),
                        () => setGateState("failed")
                    )
                setGateState("failed")
            }
        )
    }

    // lock page scroll while the drawer is open — wheel over the scrim
    // scrolled the sheet behind it. Own effect, not MUI's scroll lock: MUI
    // pads <body> to compensate the hidden scrollbar, shifting the centered
    // sheet (scrollbar-gutter: stable makes hiding free)

    useEffect(() => {
        if (!isMediumScreen || !open) return
        const prev = document.body.style.overflow
        document.body.style.overflow = "hidden"
        return () => {
            document.body.style.overflow = prev
        }
    }, [isMediumScreen, open])

    return (
        <Box className="no-print">
            <Drawer
                className="no-print"
                variant={isMediumScreen ? "temporary" : "persistent"}
                open={open}
                onClose={onClose}
                // keepMounted: a closed temporary drawer unmounts instantly —
                // crossing the breakpoint would animate slide-in but not slide-out
                ModalProps={{ keepMounted: true, disableScrollLock: true }}
                sx={{
                    ...(breakpoints.isLargeScreen ? { width: layout.sidebarWidth, flexShrink: 0 } : {}),
                    "& .MuiDrawer-paper": {
                        width: layout.sidebarWidth,
                        boxSizing: "border-box",
                        // start below the nav bar instead of sliding under it —
                        // chrome and panel never share a layer
                        top: 60,
                        height: "calc(100% - 60px)",
                        backgroundColor: `${themeSettings.surfaceColor}`,
                        backgroundImage: "none",
                        borderColor: themeSettings.borderColor,
                    },
                }}
                anchor="right"
            >
                <Box
                    sx={{
                        // extra top air: the bar's border line bears down on
                        // the first control. Sides only 16: 250px controls +
                        // a scrollbar must still fit the 300px paper
                        px: 2,
                        pt: 4.5,
                        pb: 3,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        alignItems: "center",
                        position: "relative",
                        // short viewports scroll, controls never squash
                        overflowY: "auto",
                        // the scrollbar eats ~15px of content width; nothing
                        // may ever overflow sideways here
                        overflowX: "hidden",
                        // landscape phones compress padding and rhythm before
                        // scrolling starts (nested so it wins the cascade)
                        "@media (max-height: 600px)": {
                            pt: 2,
                            pb: 2,
                            gap: 1,
                        },
                    }}
                >
                    <AudienceToggle />
                    <SkillsToggle />
                    <PhoneToggle />
                    <Button
                        variant="contained"
                        onClick={handleDownloadClick}
                        disabled={gateState === "working"}
                        sx={{
                            width: callToActionWidth,
                            height: 42,
                            flexShrink: 0,
                            textTransform: "none",
                            borderRadius: "8px",
                            fontFamily: '"DM Mono", monospace',
                            fontSize: 15,
                            fontWeight: 500,
                            gap: "8px",
                        }}
                    >
                        <svg
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                            aria-hidden="true"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ display: "block" }}
                        >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" x2="12" y1="3" y2="15" />
                        </svg>
                        download pdf
                    </Button>
                    {gateState === "failed" && (
                        <Typography variant="caption" sx={{ color: "error.main" }}>
                            Download failed — try again
                        </Typography>
                    )}
                    <Box sx={{ mt: "auto", pb: 1 }}>
                        <SocialRow />
                    </Box>
                </Box>
            </Drawer>
        </Box>
    )
}
